<template>
  <v-card class="category-alarm rounded-xl card-bg card-bd h-100 d-flex flex-column" variant="elevated" elevation="0">
    <template #title>
      <div class="d-flex justify-space-between align-center">
        <span>告警分类统计</span>
      </div>
    </template>
    <div class="d-flex flex-column flex-grow-1 overflow-hidden px-4 pb-4">
      <!-- 月份选择器 -->
      <div class="month-selector d-flex align-center ga-2 mb-4">
        <v-btn icon="mdi-chevron-left" size="small" variant="text" @click="prevMonth"></v-btn>
        <span class="month-text">{{ formatMonth(currentDate) }}</span>
        <v-btn icon="mdi-chevron-right" size="small" variant="text" @click="nextMonth"></v-btn>
      </div>

      <!-- 图表区域 -->
      <div class="chart-wrapper flex-grow-1 position-relative">
        <div ref="chartRef" class="chart-container"></div>
        <div v-if="list.length === 0" class="empty-state">
          <span class="text-grey">暂无数据</span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import * as echarts from 'echarts'
import { getAlarmTypeCount } from '@/api/alarm'
import dayjs from 'dayjs'

const currentDate = ref(new Date())
const list = ref([])
const chartRef = ref(null)
let chartInstance = null

function formatMonth(date) {
  return dayjs(date).format('YYYY年MM月')
}

function prevMonth() {
  currentDate.value = dayjs(currentDate.value).subtract(1, 'month').toDate()
  fetchData()
}

function nextMonth() {
  currentDate.value = dayjs(currentDate.value).add(1, 'month').toDate()
  fetchData()
}

function getLevelColor(level) {
  const colors = {
    1: '#94A3B8',
    2: '#4ABE65',
    3: '#3B82F6',
    4: '#F67A3C',
    5: '#E3001F',
  }
  return colors[level] || '#6B7280'
}

function getChartOption(data) {
  const yNames = data.map(item => item.alarmName).reverse()
  const chartData = data.map(item => ({
    value: item.count,
    name: item.alarmName,
    itemStyle: {
      color: getLevelColor(item.alarmLevel)
    }
  })).reverse()

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      }
    },
    grid: {
      left: '3%',
      right: '18%',
      bottom: '3%',
      top: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      splitLine: {
        show: false,
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#9CA3AF',
        fontSize: 11,
        // hideOverlap: true
      },
      minInterval: 1,
      splitNumber: 3
    },
    yAxis: {
      type: 'category',
      data: yNames,
      axisTick: {
        show: false
      },
      axisLine: {
        show: false
      },
      axisLabel: {
        color: '#374151',
        fontSize: 12,
      }
    },
    series: [
      {
        type: 'bar',
        barWidth: 12,
        showBackground: true,
        backgroundStyle: {
          color: '#F3F4F6',
          borderRadius: 6,
        },
        data: chartData,
        itemStyle: {
          borderRadius: 6,
        },
        label: {
          show: true,
          position: 'right',
          color: '#6B7280',
          fontSize: 12,
          formatter: '{c} 条'
        },
      },
    ]
  }
}

function initChart() {
  if (!chartRef.value) return

  chartInstance = echarts.init(chartRef.value)
  chartInstance.setOption(getChartOption([]))

  const resizeObserver = new ResizeObserver(() => {
    chartInstance?.resize()
  })
  resizeObserver.observe(chartRef.value)
}

function updateChart() {
  if (chartInstance) {
    chartInstance.setOption(getChartOption(list.value))
  }
}

async function fetchData() {
  try {
    const res = await getAlarmTypeCount({
      startTime: dayjs(currentDate.value).startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endTime: dayjs(currentDate.value).endOf('month').format('YYYY-MM-DD HH:mm:ss'),
    })
    if (res.code === 200 && res.data) {
      list.value = res.data
      updateChart()
    }
  } catch (error) {
    console.error('Failed to fetch alarm type count:', error)
  }
}

onMounted(() => {
  initChart()
  fetchData()
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>

<style scoped lang="scss">
.category-alarm {
  :deep(.v-card-item) {
    padding: 24px 28px 10px 28px !important;

    .v-card-title {
      font-weight: 400 !important;
      font-size: 20px !important;
    }
  }
}

.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.month-selector {
  justify-content: center;
}

.month-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  min-width: 100px;
  text-align: center;
}

.chart-wrapper {
  overflow: hidden;
  position: relative;
}

.chart-container {
  width: 100%;
  height: 100%;
}

.empty-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
}
</style>
